import {
  CHESTNY_ZNAK_LABELS_RU,
  CHESTNY_ZNAK_VALUES,
  CUSTOMS_STATUS_LABELS_RU,
  type ChestnyZnak,
  type CustomsCheckStatus,
  type CustomsQueueCounts,
  type CustomsQueueTab,
  type CustomsUserRef,
} from '@crm/shared'
import {
  CUSTOMS_MATRIX_OPTIONS,
  TAB_STATUSES,
  type CustomsMatrixOption,
  type CustomsQueueFilters,
} from '../useCustomsQueue'

const TABS: { id: CustomsQueueTab; label: string }[] = [
  { id: 'active', label: 'Активные' },
  { id: 'done', label: 'Обработано' },
  { id: 'all', label: 'Все' },
]

const SELECT_CLS =
  'px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white'

interface Props {
  filters: CustomsQueueFilters
  onChange: (patch: Partial<CustomsQueueFilters>) => void
  onReset: () => void
  hasActiveFilters: boolean
  searchInput: string
  onSearchInput: (value: string) => void
  counts: CustomsQueueCounts | null
  managers: CustomsUserRef[]
}

/**
 * Вкладки и серверные фильтры очереди нетарифки: всё фильтруется на стороне
 * базы (очередь большая — тысячи позиций), фильтры колонок грида работают
 * только по загруженным строкам.
 */
export function CustomsToolbar({
  filters,
  onChange,
  onReset,
  hasActiveFilters,
  searchInput,
  onSearchInput,
  counts,
  managers,
}: Props) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 flex-wrap border-b border-gray-200" role="tablist">
        {TABS.map((t) => {
          const active = filters.tab === t.id
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange({ tab: t.id })}
              className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${
                active
                  ? 'border-brand-600 text-brand-700 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
              {counts && (
                <span className="ml-1.5 text-xs text-gray-400 tabular-nums">
                  {counts[t.id].toLocaleString('ru-RU')}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={searchInput}
          onChange={(e) => onSearchInput(e.target.value)}
          placeholder="Поиск: партномер / производитель / № заявки / код клиента"
          aria-label="Поиск по очереди"
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white flex-1 min-w-[200px] max-w-md"
        />
        <select
          value={filters.managerId}
          onChange={(e) => onChange({ managerId: e.target.value })}
          aria-label="Менеджер"
          className={SELECT_CLS}
        >
          <option value="">Все менеджеры</option>
          {managers.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name ?? '—'}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => onChange({ status: e.target.value as CustomsCheckStatus | '' })}
          aria-label="Статус"
          className={SELECT_CLS}
        >
          <option value="">Все статусы</option>
          {TAB_STATUSES[filters.tab].map((s) => (
            <option key={s} value={s}>
              {CUSTOMS_STATUS_LABELS_RU[s]}
            </option>
          ))}
        </select>
        <select
          value={filters.matrix}
          onChange={(e) => onChange({ matrix: e.target.value as CustomsMatrixOption | '' })}
          aria-label="Матрица"
          className={SELECT_CLS}
        >
          <option value="">Все матрицы</option>
          {CUSTOMS_MATRIX_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          value={filters.chestnyZnak}
          onChange={(e) => onChange({ chestnyZnak: e.target.value as ChestnyZnak | '' })}
          aria-label="Честный знак"
          className={SELECT_CLS}
        >
          <option value="">Честный знак: все</option>
          {CHESTNY_ZNAK_VALUES.map((z) => (
            <option key={z} value={z}>
              Честный знак: {CHESTNY_ZNAK_LABELS_RU[z].toLowerCase()}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onChange({ attention: !filters.attention })}
          aria-pressed={filters.attention}
          title="Лицензия, нотификация, повышенный сбор или «Честный знак»"
          className={`px-3 py-1.5 rounded-lg text-sm border transition-colors whitespace-nowrap ${
            filters.attention
              ? 'border-red-200 bg-red-50 text-red-700 font-medium'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Внимание
        </button>
        <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
          <input
            type="checkbox"
            checked={filters.hideArchived}
            onChange={(e) => onChange({ hideArchived: e.target.checked })}
            className="rounded border-gray-300 text-brand-600"
          />
          Скрыть архивные заявки
        </label>
        <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
          Заявки с
          <input
            type="date"
            value={filters.requestFrom}
            onChange={(e) => onChange({ requestFrom: e.target.value })}
            aria-label="Заявки с даты"
            className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          />
        </label>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-gray-400 hover:text-gray-700"
          >
            × Очистить
          </button>
        )}
      </div>
    </div>
  )
}
